import React, { useState, useEffect, useCallback } from 'react';
import { usd, limitValue } from './commercialFormat';

/**
 * GOV13 — SuperAdmin Tenant-360 Commercial workspace (Slices 2+3).
 *
 * Slice 2 renders the 8 governed sections from the canonical READ projections only. Slice 3 wires every governed
 * action to the EXISTING canonical mutation endpoints behind a REVIEW/CONFIRMATION dialog — no first-click
 * execution, one idempotency key per confirmed operation, and honest write-gate handling:
 *   SOURCE_ACTION_IMPLEMENTED = YES,  RUNTIME_MUTATION_AVAILABLE = (writeEnabled && tenant not read-only).
 * Review/preview (read-only) is always available; the FINAL confirm is gated OFF while the monetization write
 * gate is OFF (or the tenant is a read-only acceptance tenant, e.g. FJON79W1) — it shows the canonical gate
 * message and never converts a 503 into success, never re-fires a disabled endpoint.
 *
 * Endpoints — read: /tenant/{ref}/commercial|plans|eligible-add-ons|packs, /tenants/{ref}/overrides,
 *   /audit?tenant_id=. mutate (all pre-existing, write-gated server-side): /tenant/{ref}/plan-change/preview
 *   (read) + /plan-change/schedule, /add-ons/no-charge, /add-ons/{id}/change-quantity|cancel-at-period-end|
 *   revoke, /requests/{id}/review, /tenants/{ref}/overrides (grant/deny).
 */
const PILL = {
  active: 'bg-green-100 text-green-800', paid: 'bg-green-100 text-green-800',
  cancel_at_period_end: 'bg-amber-100 text-amber-800', past_due: 'bg-red-100 text-red-800',
  payment_failed: 'bg-red-100 text-red-800', disputed: 'bg-red-100 text-red-800',
  refunded: 'bg-gray-200 text-gray-700', reversed: 'bg-gray-200 text-gray-700',
  revoked: 'bg-gray-200 text-gray-700', expired: 'bg-gray-200 text-gray-700',
  cancelled: 'bg-gray-200 text-gray-700', pending: 'bg-blue-100 text-blue-800',
  inactive: 'bg-gray-100 text-gray-600', not_applicable: 'bg-gray-100 text-gray-600',
  upgrade: 'bg-green-100 text-green-800', downgrade: 'bg-amber-100 text-amber-800',
  same_plan: 'bg-gray-100 text-gray-600', lateral: 'bg-blue-100 text-blue-800',
  grant: 'bg-green-100 text-green-800', deny: 'bg-red-100 text-red-800', limit_override: 'bg-blue-100 text-blue-800',
  // commercial-request lifecycle states
  requested: 'bg-blue-100 text-blue-800', under_review: 'bg-blue-100 text-blue-800',
  more_information_required: 'bg-amber-100 text-amber-800', approved_pending_billing: 'bg-green-100 text-green-800',
  completed: 'bg-green-100 text-green-800', declined: 'bg-red-100 text-red-800',
  // payment / fulfilment states
  unpaid: 'bg-amber-100 text-amber-800', fulfilled: 'bg-green-100 text-green-800', unfulfilled: 'bg-gray-100 text-gray-600',
  // wallet-health states
  healthy: 'bg-green-100 text-green-800', low: 'bg-amber-100 text-amber-800',
  critical: 'bg-red-100 text-red-800', depleted: 'bg-gray-200 text-gray-700',
};
const Pill = ({ v }) => v == null ? <span className="text-gray-400">—</span>
  : <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${PILL[v] || 'bg-gray-100 text-gray-600'}`}>{String(v).replace(/_/g, ' ')}</span>;

// Shown ONLY while an incident operator has engaged the emergency stop (default OFF → never shown in normal
// staging). Normal commercial governance — review, request-more-info, approval, decline, no-charge grant/change/
// revoke — is NOT gated by any toggle; it is always available to an authorized SuperAdmin.
// The write GATE being off is the staging write-lock — NOT an emergency stop (that is a distinct DENY_REASON / policy
// state below). Use the truthful staging message so the two conditions are never conflated.
const WRITE_DISABLED_MSG = 'Commercial writes are disabled in this staging environment.';
// Read-only / locked state is decided by the GOVERNED backend tenant mutation policy (no hardcoded tenant id).
const POLICY_DENY_MSG = {
  READ_ONLY_BASELINE: 'This tenant is preserving a read-only test baseline; commercial mutations are locked by governed policy.',
  SUSPENDED: 'This tenant is under a governed commercial suspension; mutations are locked.',
  PRODUCTION_PROTECTED: 'This tenant is production-protected; staging mutations are not permitted.',
  CONTROLLED_TEST: 'This tenant is under a controlled-test authorization; mutations require an approved acceptance run.',
};
const POLICY_STATES = ['NORMAL', 'CONTROLLED_TEST', 'READ_ONLY_BASELINE', 'SUSPENDED'];
// Owner-safe error copy — never a raw stack/secret; specific per governed code.
const ERR_MSG = {
  TENANT_MUTATION_POLICY_DENIED: 'This tenant policy does not allow this operation for the current acceptance run.',
  ASSIGNMENT_NOT_FOUND: 'The active assignment could not be resolved. Refresh and retry after the record is reconciled.',
  COMMERCIAL_MONETIZATION_WRITE_DISABLED: 'Commercial writes are disabled in this staging environment.',
  COMMERCIAL_EMERGENCY_STOP_ACTIVE: 'Payments are temporarily paused by an operator (emergency stop). Please try again shortly.',
  CONFLICTING_ACTIVE_ASSIGNMENT: 'This add-on is already assigned. Use Change quantity or Revoke.',
  ADD_ON_VERSION_ID_REQUIRED: 'An add-on version is required.',
  APPROVER_AND_REASON_REQUIRED: 'A governance reason is required.',
  REASON_REQUIRED: 'A governance reason is required.',
  QUANTITY_INVALID: 'Quantity must be a positive whole number.',
  ADDON_NOT_PUBLISHED: 'This add-on is not published in the governed catalogue.',
  PLAN_NOT_ELIGIBLE: 'This add-on is not eligible for the tenant’s current plan.',
  // GOV13 commercial-request review owner-safe codes
  REQUEST_NOT_APPROVED_FOR_BILLING: 'This request is not approved for billing yet.',
  PRODUCT_NOT_ELIGIBLE: 'This product is not eligible for the tenant’s current plan.',
  PRODUCT_VERSION_NOT_EFFECTIVE: 'The product version is not currently effective in the governed catalogue.',
  PURCHASE_ROLE_REQUIRED: 'Payment requires an authorized Business Admin or Manager in the Business plane.',
  PRODUCT_KEY_REQUIRED: 'A product is required for this request.',
  UNSUPPORTED_REQUEST_TYPE: 'This request type is not supported.',
  DUPLICATE_PENDING_REQUEST: 'A matching request is already pending for this tenant.',
  REQUEST_APPROVAL_REQUIRED: 'This request must be approved before it can proceed.',
  USAGE_CAPACITY_EXHAUSTED: 'Usage capacity for this limit is exhausted.',
  USAGE_PACK_EXPIRED: 'This usage pack has expired.',
  REQUEST_ALREADY_PAID: 'This request has already been paid.',
  REQUEST_NOT_FOUND: 'This request could not be found. It may have already been resolved.',
};
const errText = (e) => {
  const code = e?.response?.data?.detail?.code || e?.response?.data?.code;
  return ERR_MSG[code] || (code ? `Action rejected: ${code}` : 'The action could not be completed. Please refresh and try again.');
};

// ── GOV13 commercial-request helpers (SuperAdmin reviews; NEVER pays) ──
const REQ_ACTION_LABELS = {
  review: 'Begin review', request_more_information: 'Request more info',
  approve: 'Approve for payment', decline: 'Decline', cancel: 'Cancel',
};
// Only these render as review actions — `pay` is deliberately excluded (payment is a Business-plane action).
const REQ_ACTIONS = ['review', 'request_more_information', 'approve', 'decline', 'cancel'];
const requestTypeOf = (r) => r.request_type || 'PAID_ADDON';   // legacy rows default PAID_ADDON
const requestProductLabel = (r) => r.display_name || r.add_on || r.product_key || '—';
const requestProductFields = (r) => {
  const t = requestTypeOf(r);
  if (t === 'CREDIT_PACK') return [['Wallet', r.wallet || '—'], ['Credits', limitValue(r.credits)], ['Expiry (months)', r.expiry_months ?? '—']];
  if (t === 'USAGE_PACK') return [['Limit key', (r.limit_key || '—').replace(/_/g, ' ')], ['Unit', r.unit || '—'], ['Granted qty', limitValue(r.granted_quantity)], ['Expiry policy', r.expiry_policy || '—']];
  return [['Add-on', r.add_on || r.product_key || '—'], ['Cadence', r.cadence || '—']];
};
const requestPriceText = (r) => (r.displayed_price_snapshot != null
  ? usd(r.displayed_price_snapshot)
  : (r.price != null ? `${usd(Math.round(Number(r.price) * 100))}${r.currency && r.currency !== 'USD' ? ` ${r.currency}` : ''}` : '—'));
const newIdemKey = () => (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID
  ? window.crypto.randomUUID() : `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`);

export default function TenantCommercialPanel({ api, tenantRef, writeEnabled = false }) {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState(null);
  const [addons, setAddons] = useState(null);
  const [packs, setPacks] = useState(null);
  const [grants, setGrants] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [dlg, setDlg] = useState(null);      // the ONE active review/confirmation dialog
  const [policy, setPolicy] = useState(null); // governed tenant mutation policy (backend authority)
  const [polForm, setPolForm] = useState(null); // governed policy-change dialog

  const ref = encodeURIComponent(tenantRef);
  const base = `/api/v1/super-admin/commercial`;
  // Final-mutation availability = global write gate AND the governed tenant mutation policy (no hardcoded tenant).
  const policyState = (policy && policy.effective_state) || 'NORMAL';
  const policyAllows = policy ? !!policy.mutations_allowed_now : true;
  // The commercial write gate is sourced from the backend policy read (single source of truth). The optional
  // writeEnabled prop is an override for tests/embedding; production derives it from the backend.
  const writeGateOn = (policy && policy.commercial_write_enabled != null) ? !!policy.commercial_write_enabled : !!writeEnabled;
  const canExecute = writeGateOn && policyAllows;
  const denyMsg = !policyAllows ? (POLICY_DENY_MSG[policyState] || 'Commercial mutations are denied by governed policy.')
    : (!writeGateOn ? WRITE_DISABLED_MSG : null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [c, p, a, k, g, au, pol] = await Promise.allSettled([
      api.get(`${base}/tenant/${ref}/commercial`),
      api.get(`${base}/tenant/${ref}/plans`),
      api.get(`${base}/tenant/${ref}/eligible-add-ons`),
      api.get(`${base}/tenant/${ref}/packs`),
      api.get(`${base}/tenants/${ref}/overrides`),
      api.get(`${base}/audit?tenant_id=${ref}`),
      api.get(`${base}/tenant/${ref}/mutation-policy`),
    ]);
    if (c.status === 'fulfilled') setData(c.value.data); else setError((c.reason && c.reason.message) || 'Failed to load commercial projection.');
    setPlans(p.status === 'fulfilled' ? p.value.data : null);
    setAddons(a.status === 'fulfilled' ? a.value.data : null);
    setPacks(k.status === 'fulfilled' ? k.value.data : null);
    setGrants(g.status === 'fulfilled' ? g.value.data : null);
    setAudit(au.status === 'fulfilled' ? au.value.data : null);
    setPolicy(pol.status === 'fulfilled' ? pol.value.data : null);
    setLoading(false);
  }, [api, ref, base]);
  useEffect(() => { load(); }, [load]);

  // Governed policy change (governance, NOT a commercial mutation — not gated by the write gate; SuperAdmin only).
  const submitPolicy = async () => {
    if (!polForm || polForm.busy) return;
    if (!String(polForm.reason || '').trim()) { setPolForm((f) => f && { ...f, err: 'A governance reason is required.' }); return; }
    setPolForm((f) => ({ ...f, busy: true, err: null }));
    try {
      await api.post(`${base}/tenant/${ref}/mutation-policy`, {
        mutation_policy: polForm.mutation_policy, reason: polForm.reason,
        expires_at: polForm.expires_at || null,
        allowed_operation_types: polForm.mutation_policy === 'CONTROLLED_TEST' ? ['plan_change', 'owner_grant', 'owner_deny'] : [],
      });
      setPolForm(null); setToast({ ok: true, msg: 'Tenant mutation policy updated and audited.' }); await load();
    } catch (e) {
      setPolForm((f) => f && { ...f, busy: false, err: errText(e) });
    }
  };

  // Open a review dialog. spec = {title, subtitle, fields[], reasonRequired, quantity, confirmLabel, blocker,
  //   previewOnOpen(): impactObj, execute(idemKey, {reason, quantity}): Promise}. Opening NEVER mutates.
  const openDialog = useCallback(async (spec) => {
    const d = { ...spec, key: newIdemKey(), reason: '', quantity: spec.quantity?.current ?? 1,
                busy: false, gate: false, uncertain: false, err: null, impact: null, previewing: !!spec.previewOnOpen };
    setDlg(d); setToast(null);
    if (spec.previewOnOpen) {
      try { const impact = await spec.previewOnOpen(); setDlg((cur) => cur && { ...cur, impact, previewing: false }); }
      catch (e) {
        const code = e?.response?.data?.detail?.code || e?.response?.data?.code || 'PREVIEW_UNAVAILABLE';
        setDlg((cur) => cur && { ...cur, previewing: false, err: `Preview unavailable: ${code}` });
      }
    }
  }, []);

  const confirm = useCallback(async () => {
    setDlg((cur) => {
      if (!cur || cur.busy || cur.blocker) return cur;
      if (!canExecute) return { ...cur, gate: true };
      if (cur.reasonRequired && !String(cur.reason || '').trim()) return { ...cur, err: 'A governance reason is required.' };
      return { ...cur, busy: true, gate: false, err: null, uncertain: false };
    });
  }, [canExecute]);

  // Perform the mutation once the dialog transitions to busy (idempotent; single-flight).
  useEffect(() => {
    if (!dlg || !dlg.busy) return;
    let cancelled = false;
    (async () => {
      try {
        await dlg.execute(dlg.key, { reason: dlg.reason, quantity: dlg.quantity });
        if (cancelled) return;
        setDlg(null); setToast({ ok: true, msg: 'Change saved and audited.' }); await load();
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 503) setDlg((cur) => cur && { ...cur, busy: false, gate: true });
        else if (!status) { setDlg((cur) => cur && { ...cur, busy: false, uncertain: true }); await load(); }  // unknown → refresh authoritative state, never blind resubmit
        else setDlg((cur) => cur && { ...cur, busy: false, err: errText(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [dlg && dlg.busy, dlg && dlg.key]);

  if (loading) return <p className="text-sm text-gray-400">Loading commercial workspace…</p>;
  if (error && !data) return <div className="p-3 rounded bg-red-50 border border-red-200 text-sm text-red-700" role="alert">{error}</div>;
  if (!data) return null;

  const sub = data.current_subscription;
  const bp = data.billing_profile || {};
  const planRows = (plans && plans.plans) || [];
  const addonRows = (addons && addons.eligible_add_ons) || [];
  const packRows = (packs && packs.packs) || [];
  const overrideRows = (grants && grants.overrides) || [];
  const noChargeAddons = (data.active_add_ons || []).filter((a) => a.source === 'owner_grant' || a.billing_state === 'no_charge');
  const auditRows = (audit && (audit.items || audit)) || (data.recent_commercial_audit || []);

  const post = (endpoint, body, key) =>
    api.post(`${base}/tenant/${ref}${endpoint}`, { ...(body || {}), idempotency_key: key }, { headers: { 'Idempotency-Key': key } });
  const postAbs = (path, body, key) =>
    api.post(`${base}${path}`, { ...(body || {}), idempotency_key: key }, { headers: { 'Idempotency-Key': key } });

  // ── action openers (each builds a review dialog; final confirm is gated by canExecute) ──
  const reviewPlanChange = (p) => openDialog({
    title: `Change plan → ${p.display_name}`,
    subtitle: 'Review the canonical impact before scheduling. Scheduling does not charge; the plan version pins only after billing.',
    fields: [
      ['Tenant', tenantRef], ['Current plan', `${sub?.plan_id || '—'} (${sub?.plan_version_id || '—'})`],
      ['Target plan', `${p.display_name} (${p.plan_version_id})`], ['Change impact', p.change_impact],
      ['Price', p.price == null ? 'On request' : usd(Math.round(p.price * 100))], ['Currency', (p.currency || 'USD')],
      ['Cadence', p.billing_cadence || 'monthly'], ['Effective', p.effective_timing || 'policy controlled'],
      ['Actor', 'Acting SuperAdmin (server-derived)'],
    ],
    reasonRequired: false,
    confirmLabel: 'Schedule change',
    previewOnOpen: async () => (await post('/plan-change/preview', { target_plan_version_id: p.plan_version_id }, newIdemKey())).data,
    execute: (key) => {
      const mode = p.change_impact === 'upgrade' ? 'upgrade_now' : p.change_impact === 'downgrade' ? 'downgrade_at_renewal' : 'change_at_renewal';
      const effective_at = mode === 'upgrade_now' ? new Date().toISOString() : (bp.current_period_end || new Date().toISOString());
      return post('/plan-change/schedule', { target_plan_version_id: p.plan_version_id, mode, effective_at }, key);
    },
  });

  const reviewGrantNoCharge = (a) => openDialog({
    title: `Grant no-charge add-on → ${a.name}`,
    subtitle: 'Owner-granted, no-charge assignment. No Stripe item is created; entitlement activates on grant.',
    fields: [
      ['Tenant', tenantRef], ['Add-on', `${a.name} (${a.add_on_version_id})`], ['Billing', 'No charge (owner grant)'],
      ['Compatible with plan', a.compatible_with_current_plan ? 'Yes' : 'No'], ['Actor', 'Acting SuperAdmin (server-derived)'],
    ],
    reasonRequired: true, quantity: { current: 1 }, confirmLabel: 'Grant (no charge)',
    execute: (key, { reason, quantity }) => post('/add-ons/no-charge', { add_on_version_id: a.add_on_version_id, quantity, reason }, key),
  });

  const reviewAssignmentAction = (a, action) => {
    if (action === 'change_quantity') return openDialog({
      title: `Change quantity → ${a.add_on}`, fields: [['Tenant', tenantRef], ['Add-on', a.add_on_version_id], ['Current quantity', a.quantity]],
      quantity: { current: a.quantity }, confirmLabel: 'Apply quantity',
      execute: (key, { quantity }) => post(`/add-ons/${encodeURIComponent(a.assignment_id)}/change-quantity`, { quantity }, key),
    });
    if (action === 'cancel_at_period_end') return openDialog({
      title: `Cancel at period end → ${a.add_on}`, subtitle: 'Entitlement continues until the current period ends.',
      fields: [['Tenant', tenantRef], ['Add-on', a.add_on_version_id], ['Cancellation', 'At period end']], confirmLabel: 'Cancel at period end',
      execute: (key) => post(`/add-ons/${encodeURIComponent(a.assignment_id)}/cancel-at-period-end`, {}, key),
    });
    if (action === 'revoke') return openDialog({
      title: `Revoke add-on → ${a.add_on}`, subtitle: 'Immediate revoke of the entitlement.',
      fields: [['Tenant', tenantRef], ['Add-on', a.add_on_version_id], ['Effect', 'Immediate']], reasonRequired: true, confirmLabel: 'Revoke',
      execute: (key, { reason }) => post(`/add-ons/${encodeURIComponent(a.assignment_id)}/revoke`, { reason }, key),
    });
  };

  // GOV13 commercial-request review. Transitions come from the backend allowed_actions (never recomputed here);
  // approval yields approved_pending_billing and grants nothing — payment is a Business-plane action, never here.
  const reviewRequestAction = (r, action) => {
    const allowQty = action === 'approve' && !!(r.allowed_actions && r.allowed_actions.adjust_quantity);
    openDialog({
      title: `${REQ_ACTION_LABELS[action] || action.replace(/_/g, ' ')} → ${requestProductLabel(r)}`,
      subtitle: action === 'approve'
        ? 'Approval marks the request approved & payable by the Business (approved, pending billing). SuperAdmin never charges — payment happens in the Business plane.'
        : (action === 'request_more_information' ? 'Send the request back to the requester for more information.' : undefined),
      fields: [
        ['Tenant', tenantRef], ['Request type', requestTypeOf(r).replace(/_/g, ' ')],
        ['Requester', r.requester || '—'], ['Requester role', r.requester_role || '—'],
        ['State', (r.state || '—').replace(/_/g, ' ')],
        ['Requested qty', r.requested_quantity ?? r.quantity ?? '—'],
        ...requestProductFields(r),
        ['Action', REQ_ACTION_LABELS[action] || action],
      ],
      reasonRequired: action === 'approve' || action === 'decline' || action === 'request_more_information',
      requireReasonToEnable: action === 'approve',   // governance reason required before Approve enables
      ...(allowQty ? { quantity: { current: r.approved_quantity ?? r.requested_quantity ?? r.quantity ?? 1 }, showQuantity: true, quantityLabel: 'Approved quantity (optional)' } : {}),
      confirmLabel: REQ_ACTION_LABELS[action] || action.replace(/_/g, ' '),
      execute: (key, { reason, quantity }) => post(`/requests/${encodeURIComponent(r.request_id)}/review`,
        { action, reason, ...(allowQty ? { approved_quantity: quantity } : {}) }, key),
    });
  };

  // GOV13 architecture correction: SuperAdmin does NOT initiate tenant-funded pack purchases. The pack section
  // shows the governed product + status only; purchase is initiated in the Business Portal by an Admin/Manager.

  const reviewOwnerGrant = (type) => openDialog({
    title: type === 'deny' ? 'Create denial' : 'Create owner grant',
    subtitle: 'Feature/limit grant or denial via the canonical override authority (not a Stripe item).',
    fields: [['Tenant', tenantRef], ['Type', type], ['Actor', 'Acting SuperAdmin (server-derived)']],
    reasonRequired: true, confirmLabel: type === 'deny' ? 'Create denial' : 'Create grant',
    // canonical override endpoint; final confirm still gated by canExecute this phase
    execute: (key, { reason }) => postAbs(`/tenants/${ref}/overrides`, { type, feature_key: (dlg && dlg.featureKey) || undefined, reason, commercial_basis: 'complimentary' }, key),
  });

  const ReviewBtn = ({ onClick, children, title }) => (
    <button type="button" onClick={onClick} title={title}
      className="px-2 py-0.5 text-xs border rounded text-blue-700 border-blue-200 hover:bg-blue-50" data-testid="review-action">{children}</button>
  );
  const DisabledBtn = ({ children, title, tid }) => (
    <button type="button" disabled title={title} data-testid={tid || 'disabled-action'}
      className="px-2 py-0.5 text-xs border rounded text-gray-300 border-gray-200 cursor-not-allowed">{children}</button>
  );

  return (
    <div className="space-y-5" data-testid="tenant-commercial-panel">
      {!canExecute && (
        <div className="p-3 rounded bg-amber-50 border border-amber-200 text-sm text-amber-800" role="status" data-testid="write-disabled-banner">
          {denyMsg}
          <span className="block text-xs text-amber-700 mt-0.5">Review, preview, approval, and no-charge governance remain available.</span>
        </div>
      )}
      {toast && <div className={`p-2 rounded text-sm ${toast.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`} role="status">{toast.msg}</div>}

      {/* GOVERNED TENANT MUTATION POLICY */}
      <Section title="Tenant mutation policy" action={
        <button type="button" data-testid="manage-policy" onClick={() => setPolForm({ mutation_policy: policyState, reason: '', expires_at: (policy && policy.policy && policy.policy.expires_at) || '', busy: false, err: null })}
          className="px-2 py-0.5 text-xs border rounded text-blue-700 border-blue-200 hover:bg-blue-50">Manage policy</button>}>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm" data-testid="mutation-policy">
          <Row k="Policy state" v={<Pill v={policyState.toLowerCase()} />} />
          <Row k="Mutations allowed now" v={policyAllows ? 'Yes' : 'No'} />
          <Row k="Allowed operations" v={((policy && policy.policy && policy.policy.allowed_operation_types) || []).join(', ') || '—'} />
          <Row k="Reason" v={(policy && policy.policy && policy.policy.reason) || '—'} />
          <Row k="Approved by" v={(policy && policy.policy && policy.policy.approved_by) || '—'} />
          <Row k="Effective from" v={(policy && policy.policy && policy.policy.effective_from) || '—'} />
          <Row k="Expiry" v={(policy && policy.policy && policy.policy.expires_at) || 'None'} />
          <Row k="Acceptance run" v={(policy && policy.policy && policy.policy.acceptance_run_id) || '—'} />
          <Row k="Version" v={(policy && policy.policy && policy.policy.version) ?? '—'} />
        </dl>
        <p className="mt-1 text-xs text-gray-500">Governed, audited, staging-only policy — no tenant is permanently locked in source. This is the authority that decides whether commercial mutations are permitted. Normal operations require no environment toggle.</p>
      </Section>

      {/* 1 · CURRENT SUBSCRIPTION */}
      <Section title="Current subscription">
        {!sub ? <p className="text-sm text-gray-500">No commercial subscription.</p> : (
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm">
            <Row k="Plan" v={sub.plan_id || '—'} />
            <Row k="Version (immutable)" v={<span className="font-mono">{sub.plan_version_id || '—'}</span>} />
            <Row k="Billing cadence" v={bp.billing_cadence || '—'} />
            <Row k="Billing state" v={<Pill v={bp.subscription_status} />} />
            <Row k="Current period" v={bp.current_period_start ? `${bp.current_period_start} → ${bp.current_period_end || '—'}` : '—'} />
            <Row k="Primary subscription" v={<Pill v={bp.subscription_status || 'active'} />} />
            <Row k="Cancellation" v={bp.cancel_at_period_end ? 'At period end' : 'None'} />
            <Row k="Payment recovery" v={bp.payment_recovery_state || 'None'} />
            <Row k="Grandfathered" v={sub.grandfathered ? 'Yes' : 'No'} />
            <Row k="Scheduled change" v={data.scheduled_plan_change ? `${data.scheduled_plan_change.target_plan_version_id || 'pending'} (${data.scheduled_plan_change.state || 'scheduled'})` : 'None'} />
            <Row k="Stripe mode" v="Test (staging)" />
          </dl>
        )}
        {sub && sub.grandfathered && (
          <p className="mt-2 text-xs text-amber-700">Grandfathered plan preserved — a move to a current governed plan requires explicit owner action and impact review; it is never applied automatically.</p>
        )}
      </Section>

      {/* 2 · AVAILABLE BUSINESS PLANS */}
      <Section title="Available business plans">
        {planRows.length === 0 ? <p className="text-sm text-gray-500">No governed plans available.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>{['Plan', 'Version', 'Price', 'Cadence', 'Change impact', 'Effective', 'Features', 'Eligibility', ''].map((h) => <th key={h} className="px-2 py-1 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                {planRows.map((p) => (
                  <tr key={p.plan_key} className={p.current_plan ? 'bg-blue-50/40' : ''}>
                    <td className="px-2 py-1 font-medium text-gray-900">{p.display_name || p.plan_key}{p.current_plan && <span className="ml-1 text-xs text-blue-700">(current{p.grandfathered_current_plan ? ', grandfathered' : ''})</span>}</td>
                    <td className="px-2 py-1 font-mono text-xs">{p.plan_version_id || '—'}</td>
                    <td className="px-2 py-1">{p.price == null ? 'On request' : usd(Math.round(p.price * 100))}</td>
                    <td className="px-2 py-1">{p.billing_cadence || '—'}</td>
                    <td className="px-2 py-1"><Pill v={p.change_impact} /></td>
                    <td className="px-2 py-1 text-xs">{p.effective_timing || '—'}</td>
                    <td className="px-2 py-1 text-xs">{(p.included_feature_summary && p.included_feature_summary.count) ?? 0} incl.</td>
                    <td className="px-2 py-1 text-xs">{p.current_plan ? '—' : p.eligible ? <span className="text-green-700">Eligible</span> : <span className="text-amber-700" title={p.ineligibility_reason || ''}>{(p.ineligibility_reason || 'Not eligible').replace(/_/g, ' ')}</span>}</td>
                    <td className="px-2 py-1">{!p.current_plan && p.eligible
                      ? <ReviewBtn onClick={() => reviewPlanChange(p)} title="Review plan change">Review change</ReviewBtn>
                      : (!p.current_plan && <DisabledBtn title={p.ineligibility_reason || 'Not selectable'}>Review change</DisabledBtn>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* 3 · ACTIVE ADD-ONS */}
      <Section title="Active add-ons">
        {(data.active_add_ons || []).length === 0 ? <p className="text-sm text-gray-500">No active add-ons.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>{['Add-on', 'Version', 'Qty', 'Price', 'Cadence', 'Source', 'Billing', 'Entitlement', 'Renewal', 'Actions'].map((h) => <th key={h} className="px-2 py-1 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                {data.active_add_ons.map((a, i) => (
                  <tr key={a.add_on_version_id || i}>
                    <td className="px-2 py-1 font-medium text-gray-900">{a.add_on}</td>
                    <td className="px-2 py-1 font-mono text-xs">{a.add_on_version_id}</td>
                    <td className="px-2 py-1">{a.quantity}</td>
                    <td className="px-2 py-1">{usd(a.price_snapshot)}</td>
                    <td className="px-2 py-1">{a.cadence || '—'}</td>
                    <td className="px-2 py-1 text-xs">{(a.source || 'owner grant').replace(/_/g, ' ')}</td>
                    <td className="px-2 py-1"><Pill v={a.billing_state} /></td>
                    <td className="px-2 py-1"><Pill v={a.entitlement_state} /></td>
                    <td className="px-2 py-1">{a.renewal_at || '—'}</td>
                    <td className="px-2 py-1">
                      {!a.assignment_id ? <span className="text-xs text-gray-400" title="This row has no canonical assignment id (legacy/projection-only); actions are unavailable.">—</span> : (
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(a.allowed_actions || {}).filter(([act, ok]) => ok && ['change_quantity', 'cancel_at_period_end', 'revoke'].includes(act))
                            .map(([act]) => <ReviewBtn key={act} onClick={() => reviewAssignmentAction(a, act)} title={act.replace(/_/g, ' ')}>{act === 'change_quantity' ? 'Change qty' : act === 'cancel_at_period_end' ? 'Cancel at period end' : 'Revoke'}</ReviewBtn>)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* 4 · ELIGIBLE ADD-ONS */}
      <Section title="Eligible add-ons">
        {addonRows.length === 0 ? <p className="text-sm text-gray-500">No eligible add-ons in the governed catalogue.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>{['Add-on', 'Billing', 'Price', 'Cadence', 'Compatible', 'Eligibility', ''].map((h) => <th key={h} className="px-2 py-1 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                {addonRows.map((a) => (
                  <tr key={a.add_on_version_id}>
                    <td className="px-2 py-1"><div className="font-medium text-gray-900">{a.name}</div><div className="text-xs text-gray-500">{a.description}</div></td>
                    <td className="px-2 py-1 text-xs">{(a.billing_type || '—')}</td>
                    <td className="px-2 py-1">{a.price == null ? '—' : usd(Math.round(a.price * 100))}</td>
                    <td className="px-2 py-1">{a.cadence || '—'}</td>
                    <td className="px-2 py-1">{a.compatible_with_current_plan ? <span className="text-green-700 text-xs">Yes</span> : <span className="text-red-700 text-xs">No</span>}</td>
                    <td className="px-2 py-1 text-xs">{a.current_assignment ? <span className="text-blue-700">Assigned (×{a.current_quantity ?? 1})</span> : a.eligible ? <span className="text-green-700">Eligible</span> : <span className="text-amber-700" title={a.eligibility_reason || ''}>{(a.eligibility_reason || 'Not eligible').replace(/_/g, ' ')}</span>}</td>
                    <td className="px-2 py-1">{!a.current_assignment && a.eligible
                      ? <ReviewBtn onClick={() => reviewGrantNoCharge(a)} title="Grant no-charge add-on">Grant (no charge)</ReviewBtn>
                      : (!a.eligible ? <DisabledBtn tid="incompatible-disabled" title={a.eligibility_reason || 'Not eligible'}>Grant</DisabledBtn> : null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* 5 · USAGE / CREDIT PACKS */}
      <Section title="Usage / credit packs">
        {packRows.length === 0 ? <p className="text-sm text-gray-500">No published packs.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {packRows.map((p) => (
              <div key={p.pack_id} className="border rounded p-3" data-testid="pack-card">
                <div className="flex justify-between items-start">
                  <div><p className="font-medium text-gray-900">{p.name}</p><p className="text-xs text-gray-500">{p.description}</p></div>
                  <span className="text-xs text-gray-500 text-right max-w-[11rem]" data-testid="pack-business-portal-note">Available for purchase in the Business Portal by an authorized Admin or Manager.</span>
                </div>
                <dl className="text-sm grid grid-cols-2 gap-x-4 gap-y-0.5 mt-2">
                  <Row k="Credits" v={limitValue(p.credits)} />
                  <Row k="Price" v={p.price == null ? '—' : usd(Math.round(p.price * 100))} />
                  <Row k="Wallet" v={p.wallet_bucket || '—'} />
                  <Row k="Expiry" v={p.expiry_policy || '—'} />
                  <Row k="Balance" v={p.current_balance_context ? limitValue(p.current_balance_context.available ?? p.current_balance_context.balance) : '—'} />
                  <Row k="Purchase" v={<Pill v={p.purchase_status} />} />
                </dl>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* 6 · OWNER GRANTS & DENIALS */}
      <Section title="Owner grants & denials" action={
        <div className="flex gap-1">
          <ReviewBtn onClick={() => reviewOwnerGrant('grant')} title="Create owner grant">New grant</ReviewBtn>
          <ReviewBtn onClick={() => reviewOwnerGrant('deny')} title="Create denial">New denial</ReviewBtn>
        </div>}>
        {overrideRows.length === 0 && noChargeAddons.length === 0 ? <p className="text-sm text-gray-500">No owner grants or denials.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>{['Type', 'Target', 'Value', 'Basis', 'Reason', 'Approver', 'Expiry', 'Status'].map((h) => <th key={h} className="px-2 py-1 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                {noChargeAddons.map((a, i) => (
                  <tr key={`nc${i}`}>
                    <td className="px-2 py-1"><Pill v="grant" /></td>
                    <td className="px-2 py-1">{a.add_on} <span className="text-xs text-gray-500">(no-charge add-on)</span></td>
                    <td className="px-2 py-1">×{a.quantity ?? 1}</td>
                    <td className="px-2 py-1 text-xs">complimentary</td>
                    <td className="px-2 py-1 text-xs">{a.reason || '—'}</td>
                    <td className="px-2 py-1 text-xs">{a.approver || '—'}</td>
                    <td className="px-2 py-1 text-xs">{a.expires_at || 'None'}</td>
                    <td className="px-2 py-1"><Pill v={a.entitlement_state} /></td>
                  </tr>
                ))}
                {overrideRows.map((o) => (
                  <tr key={o.id}>
                    <td className="px-2 py-1"><Pill v={o.type} /></td>
                    <td className="px-2 py-1">{o.target_display_name || o.target_key || o.feature_key || o.limit_key || o.connector_key || '—'}</td>
                    <td className="px-2 py-1">{o.value_label ?? (o.value ?? '—')}</td>
                    <td className="px-2 py-1 text-xs">{o.commercial_basis || '—'}</td>
                    <td className="px-2 py-1 text-xs">{o.reason || '—'}</td>
                    <td className="px-2 py-1 text-xs">{o.approved_by_display_name || o.approver || '—'}</td>
                    <td className="px-2 py-1 text-xs">{o.expiry || 'Permanent'}</td>
                    <td className="px-2 py-1"><Pill v={o.active === false ? 'revoked' : 'active'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* 7 · SCHEDULED & PENDING CHANGES */}
      <Section title="Scheduled & pending changes">
        <div className="mb-2 text-sm">
          <span className="text-gray-500">Scheduled plan change: </span>
          {data.scheduled_plan_change
            ? <span className="text-gray-900">{data.scheduled_plan_change.target_plan_version_id || 'pending'} ({data.scheduled_plan_change.state || 'scheduled'})</span>
            : <span className="text-gray-500">None</span>}
        </div>
        {(data.pending_requests || []).length === 0 ? <p className="text-sm text-gray-500">No pending commercial requests.</p> : (
          <div className="space-y-2" data-testid="commercial-requests">
            <p className="text-xs text-gray-500">Commercial requests (add-on, credit pack, usage pack) reviewed by the SuperAdmin. Approval marks a request payable by the Business — payment is initiated in the Business plane, never here.</p>
            {data.pending_requests.map((r, i) => {
              const acts = REQ_ACTIONS.filter((a) => r.allowed_actions && r.allowed_actions[a]);
              return (
                <div key={r.request_id || i} className="border rounded p-3" data-testid="commercial-request-card">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="font-medium text-gray-900">{requestProductLabel(r)} <span className="ml-1 text-xs text-gray-500">({requestTypeOf(r).replace(/_/g, ' ')})</span></p>
                      <p className="text-xs text-gray-500">{r.requester || '—'}{r.requester_role ? ` · ${r.requester_role}` : ''}</p>
                    </div>
                    <Pill v={r.state} />
                  </div>
                  <dl className="text-sm grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-0.5 mt-2">
                    <Row k="Quantity" v={r.approved_quantity ?? r.requested_quantity ?? r.quantity ?? '—'} />
                    <Row k="Price" v={requestPriceText(r)} />
                    <Row k="Payment" v={<Pill v={r.payment_status} />} />
                    <Row k="Fulfilment" v={<Pill v={r.fulfilment_status} />} />
                    <Row k="Reviewed by" v={r.reviewed_by || '—'} />
                    <Row k="Requester reason" v={r.reason || '—'} />
                    {r.review_reason ? <Row k="Review note" v={r.review_reason} /> : null}
                    {requestProductFields(r).map(([k, v]) => <Row key={k} k={k} v={v} />)}
                  </dl>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {acts.length === 0
                      ? <span className="text-xs text-gray-400">No review actions available at the current state.</span>
                      : acts.map((a) => <ReviewBtn key={a} onClick={() => reviewRequestAction(r, a)} title={REQ_ACTION_LABELS[a]}>{REQ_ACTION_LABELS[a]}</ReviewBtn>)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* COMMERCIAL REQUEST HISTORY */}
      <Section title="Request history">
        {(data.request_history || []).length === 0 ? <p className="text-sm text-gray-500">No request history.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>{['Type', 'Product', 'State', 'Payment', 'Fulfilment', 'Reviewed by'].map((h) => <th key={h} className="px-2 py-1 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100" data-testid="request-history">
                {data.request_history.map((r, i) => (
                  <tr key={r.request_id || i}>
                    <td className="px-2 py-1 text-xs">{requestTypeOf(r).replace(/_/g, ' ')}</td>
                    <td className="px-2 py-1 font-medium text-gray-900">{requestProductLabel(r)}</td>
                    <td className="px-2 py-1"><Pill v={r.state} /></td>
                    <td className="px-2 py-1"><Pill v={r.payment_status} /></td>
                    <td className="px-2 py-1"><Pill v={r.fulfilment_status} /></td>
                    <td className="px-2 py-1 text-xs">{r.reviewed_by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* WALLET HEALTH (read-only — SuperAdmin observes; does not purchase) */}
      <Section title="Wallet health">
        {(data.wallet_health || []).length === 0 ? <p className="text-sm text-gray-500">No wallet health data.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="wallet-health">
            {data.wallet_health.map((w, i) => (
              <div key={w.wallet_key || i} className="border rounded p-3" data-testid="wallet-health-card">
                <div className="flex justify-between items-start">
                  <p className="font-medium text-gray-900">{w.display_name || w.wallet_key || '—'}</p>
                  <Pill v={(w.health_status || '').toLowerCase() || null} />
                </div>
                <dl className="text-sm grid grid-cols-2 gap-x-4 gap-y-0.5 mt-2">
                  <Row k="Current balance" v={limitValue(w.current_balance)} />
                  <Row k="Available (unexpired)" v={w.available_unexpired_balance != null ? limitValue(w.available_unexpired_balance) : '—'} />
                  <Row k="Avg daily burn" v={w.average_daily_burn != null ? limitValue(w.average_daily_burn) : '—'} />
                  <Row k="Est. days remaining" v={(w.estimate_state === 'ESTIMATE_UNAVAILABLE' || w.estimated_days_remaining == null) ? 'Estimate unavailable' : limitValue(w.estimated_days_remaining)} />
                  <Row k="Next expiry" v={w.next_expiry_date ? `${w.next_expiry_date}${w.next_expiring_quantity != null ? ` (×${limitValue(w.next_expiring_quantity)})` : ''}` : 'None'} />
                </dl>
                <p className="mt-1 text-xs text-gray-500">Observation only — wallet top-ups are initiated by the Business, never charged here.</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* USAGE CAPACITY (per usage-pack limit_key) */}
      <Section title="Usage capacity">
        {(data.usage_capacity || []).length === 0 ? <p className="text-sm text-gray-500">No usage-pack capacity.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>{['Limit', 'Granted', 'Consumed', 'Remaining', 'Expired'].map((h) => <th key={h} className="px-2 py-1 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100" data-testid="usage-capacity">
                {data.usage_capacity.map((u, i) => (
                  <tr key={u.limit_key || i}>
                    <td className="px-2 py-1 font-medium text-gray-900">{(u.limit_key || '—').replace(/_/g, ' ')}</td>
                    <td className="px-2 py-1">{limitValue(u.granted_quantity)}</td>
                    <td className="px-2 py-1">{limitValue(u.consumed_quantity)}</td>
                    <td className="px-2 py-1">{limitValue(u.remaining_quantity)}</td>
                    <td className="px-2 py-1">{limitValue(u.expired_quantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* 8 · COMMERCIAL AUDIT HISTORY */}
      <Section title="Commercial audit history">
        {(auditRows || []).length === 0 ? <p className="text-sm text-gray-500">No commercial audit entries for this tenant.</p> : (
          <ul className="text-xs text-gray-700 space-y-0.5" data-testid="commercial-audit">
            {auditRows.slice(0, 25).map((e, i) => (
              <li key={i} className="font-mono">
                {(e.timestamp || e.at || '—')} · {e.action_label || e.action} {e.previous_state != null ? `(${e.previous_state} → ${e.resulting_state})` : ''} {(e.actor_display_name || e.actor) ? `· ${e.actor_display_name || e.actor}` : ''} {e.reason ? `· ${e.reason}` : ''}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {dlg && <ConfirmDialog dlg={dlg} setDlg={setDlg} onConfirm={confirm} canExecute={canExecute} gateMsg={denyMsg || WRITE_DISABLED_MSG} />}

      {polForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" data-testid="policy-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b"><h3 className="font-semibold text-gray-900">Governed tenant mutation policy</h3>
              <p className="text-xs text-gray-500 mt-1">Staging-only governance for {String(tenantRef)} — audited and versioned. Not a commercial mutation; creates no Stripe object.</p></div>
            <div className="p-4 space-y-2">
              <label className="block text-sm">Policy state
                <select value={polForm.mutation_policy} data-testid="policy-state-select"
                  onChange={(e) => setPolForm((f) => f && { ...f, mutation_policy: e.target.value })}
                  className="mt-0.5 w-full border rounded px-2 py-1 text-sm">
                  {POLICY_STATES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select></label>
              <label className="block text-sm">Expiry (optional, ISO)
                <input type="text" value={polForm.expires_at} placeholder="2026-09-08T00:00:00Z"
                  onChange={(e) => setPolForm((f) => f && { ...f, expires_at: e.target.value })}
                  className="mt-0.5 w-full border rounded px-2 py-1 text-sm" /></label>
              <label className="block text-sm">Governance reason (required)
                <textarea value={polForm.reason} data-testid="policy-reason"
                  onChange={(e) => setPolForm((f) => f && { ...f, reason: e.target.value })}
                  className="mt-0.5 w-full border rounded px-2 py-1 text-sm" rows={2} /></label>
              {polForm.err && <div className="p-2 rounded bg-red-50 border border-red-200 text-xs text-red-700" data-testid="policy-error">{polForm.err}</div>}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button type="button" onClick={() => setPolForm(null)} className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50">Cancel</button>
              <button type="button" onClick={submitPolicy} disabled={polForm.busy} data-testid="policy-confirm"
                className={`px-3 py-1.5 text-sm rounded text-white ${polForm.busy ? 'bg-gray-300' : 'bg-blue-600 hover:bg-blue-700'}`}>
                {polForm.busy ? 'Saving…' : 'Save policy'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfirmDialog({ dlg, setDlg, onConfirm, canExecute, gateMsg }) {
  const close = () => setDlg(null);
  const finalDisabled = dlg.busy || !!dlg.blocker || !canExecute
    || (dlg.reasonRequired && dlg.requireReasonToEnable && !String(dlg.reason || '').trim());
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" data-testid="confirm-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b"><h3 className="font-semibold text-gray-900">{dlg.title}</h3>
          {dlg.subtitle && <p className="text-xs text-gray-500 mt-1">{dlg.subtitle}</p>}</div>
        <div className="p-4 space-y-2">
          <dl className="text-sm grid grid-cols-1 gap-y-1">
            {(dlg.fields || []).map(([k, v], i) => (
              <div key={i} className="flex justify-between gap-4 border-b border-gray-100 py-0.5"><dt className="text-gray-500">{k}</dt><dd className="text-gray-900 font-medium text-right">{String(v)}</dd></div>
            ))}
          </dl>
          {dlg.previewing && <p className="text-xs text-gray-400">Loading canonical impact preview…</p>}
          {dlg.impact && (
            <div className="rounded bg-gray-50 border p-2 text-xs" data-testid="impact-preview">
              <p className="font-semibold text-gray-600 mb-1">Canonical impact</p>
              <dl className="grid grid-cols-1 gap-y-0.5">
                {Object.entries(dlg.impact).filter(([, v]) => v != null && typeof v !== 'object').map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4"><dt className="text-gray-500">{k.replace(/_/g, ' ')}</dt><dd className="text-gray-900">{String(v)}</dd></div>
                ))}
              </dl>
            </div>
          )}
          {typeof dlg.quantity !== 'undefined' && (dlg.showQuantity || (dlg.confirmLabel && /quantity|Change qty|Apply quantity|Grant/i.test(dlg.confirmLabel))) && (
            <label className="block text-sm">{dlg.quantityLabel || 'Quantity'}
              <input type="number" min="1" value={dlg.quantity} onChange={(e) => setDlg((c) => c && { ...c, quantity: parseInt(e.target.value, 10) || 1 })}
                className="mt-0.5 w-full border rounded px-2 py-1 text-sm" data-testid="quantity-input" /></label>
          )}
          {dlg.reasonRequired && (
            <label className="block text-sm">Governance reason (required)
              <textarea value={dlg.reason} onChange={(e) => setDlg((c) => c && { ...c, reason: e.target.value })}
                className="mt-0.5 w-full border rounded px-2 py-1 text-sm" rows={2} data-testid="reason-input" /></label>
          )}
          {dlg.blocker && <div className="p-2 rounded bg-gray-100 text-xs text-gray-600" data-testid="dialog-blocker">This action is not yet available at the SuperAdmin tenant plane: <span className="font-mono">{dlg.blocker}</span></div>}
          {dlg.gate && <div className="p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="dialog-gate">{gateMsg}</div>}
          {dlg.uncertain && <div className="p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="dialog-uncertain">Result unknown — the authoritative state was refreshed. Re-open to retry if the change did not apply.</div>}
          {dlg.err && <div className="p-2 rounded bg-red-50 border border-red-200 text-xs text-red-700" data-testid="dialog-error">{dlg.err}</div>}
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button type="button" onClick={close} className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50" data-testid="dialog-cancel">Cancel</button>
          <button type="button" onClick={onConfirm} disabled={finalDisabled} data-testid="dialog-confirm"
            title={!canExecute ? gateMsg : (dlg.blocker || '')}
            className={`px-3 py-1.5 text-sm rounded text-white ${finalDisabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {dlg.busy ? 'Working…' : (dlg.confirmLabel || 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between py-0.5 border-b border-gray-100">
    <dt className="text-gray-500">{k}</dt><dd className="text-gray-900 font-medium">{v}</dd>
  </div>
);
const Section = ({ title, action, children }) => (
  <section>
    <div className="flex items-center justify-between mb-2">
      <h4 className="text-xs font-semibold text-gray-500 uppercase">{title}</h4>
      {action}
    </div>
    {children}
  </section>
);
